import { KeycloakEventType, KeycloakService } from 'keycloak-angular';
import { Inject, InjectionToken } from '@angular/core';

/**
 * Keycloak configuration interface
 */
export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

/**
 * Optional callback function that gets called after Keycloak initialization
 */
export type KeycloakInitCallback = () => void;

/**
 * Injection token for Keycloak configuration
 */
export const KEYCLOAK_CONFIG = new InjectionToken<KeycloakConfig>(
  'KEYCLOAK_CONFIG',
);

/** localStorage key used to broadcast logout to other tabs */
const CROSS_TAB_LOGOUT_KEY = 'keycloak-cross-tab-logout';

/** sessionStorage key - set when logout was received while tab was in background (show alert when user switches to tab) */
const PENDING_LOGOUT_ALERT_KEY = 'keycloak-pending-logout-alert';

/** localStorage key used to broadcast login to other tabs (so they refresh and show logged-in state) */
const CROSS_TAB_LOGIN_KEY = 'keycloak-cross-tab-login';

/** Keys to clear from localStorage when logout is detected in another tab */
const TOKEN_KEYS_TO_CLEAR = ['iudx-ui-cat-auth-token', 'ai-sandbox-cat-auth-token', 'userData'];

/** localStorage keys that store auth tokens - updated when token is refreshed on expiry */
const AUTH_TOKEN_KEYS = ['iudx-ui-cat-auth-token', 'ai-sandbox-cat-auth-token'];

/**
 * Initialize Keycloak service
 * @param keycloak KeycloakService instance
 * @param config Keycloak configuration
 * @param onInitCallback Optional callback function to execute after successful initialization
 * @returns Factory function for APP_INITIALIZER
 */
export function initializeKeycloak(
  keycloak: KeycloakService,
  config: KeycloakConfig,
  onInitCallback?: KeycloakInitCallback,
) {
  return () => {
    const pageLoadTime = Date.now();

    // Listen for login/logout in other tabs
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === CROSS_TAB_LOGIN_KEY && e.newValue) {
        // Login happened in another tab - refresh to show logged-in state
        window.location.reload();
      }
      if (e.key === CROSS_TAB_LOGOUT_KEY && e.newValue) {
        for (const key of TOKEN_KEYS_TO_CLEAR) {
          try {
            localStorage.removeItem(key);
          } catch {
            // Ignore
          }
        }
        // Browsers block alert() in background tabs - defer until user switches to this tab
        if (document.visibilityState === 'visible') {
          alert("You've been logged out from another tab or window.");
        } else {
          sessionStorage.setItem(PENDING_LOGOUT_ALERT_KEY, 'true');
        }
        window.location.href = '/';
      }
    });

    // When user switches to this tab, check for login broadcast or pending logout alert
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      try {
        // Show logout alert if we received logout while in background (browsers block alert in background tabs)
        if (sessionStorage.getItem(PENDING_LOGOUT_ALERT_KEY)) {
          sessionStorage.removeItem(PENDING_LOGOUT_ALERT_KEY);
          alert("You've been logged out from another tab or window.");
        }
        // Check if login happened in another tab
        const loginTs = localStorage.getItem(CROSS_TAB_LOGIN_KEY);
        if (loginTs && parseInt(loginTs, 10) > pageLoadTime) {
          window.location.reload();
        }
      } catch {
        // Ignore
      }
    });

    // Show pending logout alert on page load if we're already visible (e.g. user switched to tab before redirect completed)
    try {
      if (sessionStorage.getItem(PENDING_LOGOUT_ALERT_KEY) && document.visibilityState === 'visible') {
        sessionStorage.removeItem(PENDING_LOGOUT_ALERT_KEY);
        setTimeout(() => alert("You've been logged out from another tab or window."), 0);
      }
    } catch {
      // Ignore
    }

    // Wrap logout to broadcast to other tabs before redirecting
    const originalLogout = keycloak.logout.bind(keycloak);
    (keycloak as { logout: typeof originalLogout }).logout = async (redirectUri?: string) => {
      try {
        localStorage.setItem(CROSS_TAB_LOGOUT_KEY, Date.now().toString());
      } catch {
        // Ignore if localStorage is unavailable
      }
      return originalLogout(redirectUri ?? window.location.origin);
    };

    // Mark Keycloak as initializing
    sessionStorage.setItem('keycloak-initializing', 'true');

    keycloak.keycloakEvents$.subscribe({
      next: (e) => {
        if (e.type == KeycloakEventType.OnTokenExpired) {
          // Check if any auth token key exists (iudx-ui-cat-auth-token or ai-sandbox-cat-auth-token)
          const hasStoredToken = AUTH_TOKEN_KEYS.some(
            (key) => !!localStorage.getItem(key),
          );
          if (hasStoredToken) {
            keycloak.getToken().then(
              (token) => {
                const bearerToken = 'Bearer ' + token;
                for (const key of AUTH_TOKEN_KEYS) {
                  try {
                    localStorage.setItem(key, bearerToken);
                  } catch {
                    // Ignore if localStorage is unavailable
                  }
                }
              },
              (err) => {
                console.error(
                  'Token refresh by getToken failed: ',
                  err,
                );
              },
            );
          }
        }
      },
    });
    // Timeout so app boots even if Keycloak is slow (incognito, slow network)
    const INIT_TIMEOUT_MS = 8000;
    const timeoutPromise = new Promise<void>((resolve) => {
      setTimeout(() => {
        sessionStorage.removeItem('keycloak-initializing');
        resolve();
      }, INIT_TIMEOUT_MS);
    });

    const keycloakInitPromise = keycloak
      .init({
        config: config,
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false, // Disable iframe check to prevent 502 loops
          checkLoginIframeInterval: 25,
          enableLogging: false, // Disable logging in production
        },
        loadUserProfileAtStartUp: false, // Disable to prevent 502 errors on refresh
      })
      .then(async () => {
        // Clear initialization flag
        sessionStorage.removeItem('keycloak-initializing');

        // Call the optional callback function
        if (onInitCallback) {
          onInitCallback();
        }

        // --- Add Keycloak JS event hooks for cross-tab/session handling ---
        const kc: any = keycloak.getKeycloakInstance();
        if (kc) {
          kc.onAuthLogout = () => {
            // Logout detected (e.g. from another tab, SSO session end, or token invalidated)
            alert('You have been logged out (from another tab or window).');
            window.location.href = '/';
          };
          kc.onTokenExpired = async () => {
            // Avoid logging users out on transient network/keycloak hiccups.
            // Retry refresh a couple of times before deciding auth is truly invalid.
            const maxAttempts = 3;
            let lastError: any = null;

            const isLikelyTransientError = (err: any): boolean => {
              const msg = String(err?.message || err || '').toLowerCase();
              const status = err?.status ?? err?.error?.status;
              return (
                status === 0 ||
                msg.includes('network') ||
                msg.includes('failed to fetch') ||
                msg.includes('timeout') ||
                msg.includes('load failed') ||
                msg.includes('bad gateway') ||
                msg.includes('502') ||
                msg.includes('503') ||
                msg.includes('504')
              );
            };

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              try {
                // updateToken() returns true when refreshed, false when still valid.
                // "false" is NOT a failure and should not force logout.
                await keycloak.updateToken(5);
                return;
              } catch (err) {
                lastError = err;
                if (attempt < maxAttempts) {
                  await new Promise((resolve) => setTimeout(resolve, attempt * 500));
                  continue;
                }
              }
            }

            if (isLikelyTransientError(lastError)) {
              console.warn('Token refresh failed due to transient error; keeping session active.');
              return;
            }

            console.error('Token refresh failed with non-transient error, logging out:', lastError);
            await keycloak.logout(window.location.origin);
          };
          kc.onAuthSuccess = () => {
            // Clean up URL parameters and hash fragments after successful authentication
            const hasHash = window.location.hash && window.location.hash.length > 1;
            const hasSearch = window.location.search && window.location.search.length > 1;
            
            if (hasHash || hasSearch) {
              // Parse hash fragment (remove # and parse as URLSearchParams)
              let hashParams = new URLSearchParams();
              if (hasHash) {
                const hashString = window.location.hash.substring(1);
                // Handle both #param=value and #?param=value formats
                const hashToParse = hashString.startsWith('?') ? hashString.substring(1) : hashString;
                try {
                  hashParams = new URLSearchParams(hashToParse);
                } catch (e) {
                  console.warn('Failed to parse hash params:', e);
                }
              }
              
              // Parse search params
              const searchParams = new URLSearchParams(window.location.search);
              
              // Check if this is an auth callback (has code, state, or error in either hash or search)
              const hasAuthParams = 
                hashParams.has('code') || hashParams.has('state') || hashParams.has('error') ||
                searchParams.has('code') || searchParams.has('state') || searchParams.has('error');
              
              if (hasAuthParams) {
                // Broadcast login to other tabs so they refresh and show logged-in state
                try {
                  localStorage.setItem(CROSS_TAB_LOGIN_KEY, Date.now().toString());
                } catch {
                  // Ignore if localStorage is unavailable
                }
                // Check for rapid reload prevention
                const lastReloadTime = sessionStorage.getItem('keycloak-last-reload-time');
                const now = Date.now();
                const authProcessed = sessionStorage.getItem('keycloak-auth-processed');
                
                // Prevent rapid reloads (less than 2 seconds apart)
                if (lastReloadTime && (now - parseInt(lastReloadTime)) < 2000) {
                  // Just clean URL and return
                  const cleanUrl = window.location.origin + window.location.pathname;
                  window.history.replaceState({}, '', cleanUrl);
                  return;
                }
                
                // Clean URL immediately - remove both hash and search params
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, '', cleanUrl);
                
                // Only reload on FIRST explicit login redirect, never on subsequent ones
                if (!authProcessed) {
                  // Mark as processed and record time
                  sessionStorage.setItem('keycloak-auth-processed', 'true');
                  sessionStorage.setItem('keycloak-last-reload-time', now.toString());
                  
                  // Small delay to ensure URL is cleaned before reload
                  setTimeout(() => {
                    window.location.reload();
                  }, 100);
                } else {
                  // Already processed - URL already cleaned, no reload needed
                }
              } else {
                // No auth params, just clean the URL
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, '', cleanUrl);
              }
            }
            
            // Clear processing flag after delay to allow for future logins
            setTimeout(() => {
              sessionStorage.removeItem('keycloak-auth-processed');
            }, 5000);
          };
        }
        // --- End Keycloak JS event hooks ---
      })
      .catch((err) => {
        sessionStorage.removeItem('keycloak-initializing');
        const msg = err?.message ?? '';
        const isUnavailable =
          err?.status === 502 ||
          msg.includes('Bad Gateway') ||
          msg.includes('502') ||
          msg.includes('Failed to fetch') ||
          msg.includes('Load failed');
        if (isUnavailable) {
          sessionStorage.removeItem('keycloak-auth-processed');
          sessionStorage.removeItem('keycloak-last-processed-url');
        }
      });

    // Boot app after Keycloak completes or timeout (faster in incognito when Keycloak is slow)
    return Promise.race([keycloakInitPromise, timeoutPromise]);
  };
}
